import { createAsyncThunk } from "@reduxjs/toolkit";

import { instance } from "../../axios/axios";
import { sanitizeFileName, UPLOAD_CATEGORY } from "./../../lib/utils";

export const sendMassMessage = createAsyncThunk(
  "send/MassMessage",
  async ({ text, location }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const { data } = await instance.post(
        "user/portal/send-mass-message",
        { text, location },
        config
      );
      return data;
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchGroupMessageFilters = createAsyncThunk(
  "massMessage/fetchFilters",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        "user/portal/get-group-message-filters",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = data.data ?? {};

      // Employees now come from the MySQL mirror (same source as sendTimedMassMessages),
      // so position, location, and team map directly to their own fields — no EPL parsing.
      return {
        locationOptions: (raw.locations ?? []).map((r) => ({
          id:    r.id,
          label: r.fields["Location"] ?? r.id,
        })),
        positionOptions: (raw.positions ?? []).map((r) => ({
          id:    r.id,
          label: r.fields["Position"] ?? r.id,
        })),
        employeeOptions: (raw.employees ?? []).map((r) => ({
          id:       r.id,
          label:    r.fields["Name & ID"] ?? r.fields["Name"] ?? r.id,
          name:     r.fields["Name"] ?? "",
          team:     (r.fields["Team"] ?? "").trim(),
          location: (r.fields["Location (Text)"] ?? "").trim(),
          position: (r.fields["Position (TEXT)"] ?? "").trim(),
        })),
        categories: raw.categories ?? [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ?? error.message
      );
    }
  }
);


export const generatePresignedUrls = async (files, token, userInfo) => {
  const urls = [];
  for (let file of files) {
    const encodedFileName = sanitizeFileName(file.name);
    const res = await instance.post(
      "/uploadDocs/portal/generate-url-upload",
      {
        fileName: encodedFileName,
        metadata: {
          role:     userInfo?.["Position (Text)"],
          userId:   userInfo?.["Employee ID"],
          fileType: file?.type,
          // Same UPLOAD_CATEGORY family used by training-task proof-of-completion uploads.
          // Files land in the same GCS bucket; the link is appended to the SMS body
          // by the BFF — Kindred never sends MMS attachments.
          category: UPLOAD_CATEGORY.MASS_MESSAGE,
        },
      },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
    );
    urls.push({ file, url: res?.data?.data?.url, fields: res?.data?.data?.fields });
  }
  return urls;
};

export const uploadMessageFiles = async (urls) => {
  const uploaded = [];
  for (let { file, url, fields } of urls) {
    const formData = new FormData();
    Object.keys(fields).forEach((key) => formData.append(key, fields[key]));
    formData.append("file", file);
    const res = await instance.post(url, formData, { headers: { "Content-Type": "multipart/form-data" } });
    if (res.status === 204) {
      uploaded.push({ filename: fields.key, url: `${url}${fields.key}` });
    } else {
      // GCS returns 204 on a successful signed-POST upload. Any other status
      // means the file did NOT land — throw so the caller surfaces "File upload
      // failed" instead of silently sending the mass message WITHOUT its
      // attachment (the "Please see attached document" with no document bug).
      throw new Error(`GCS upload failed with status ${res.status}`);
    }
  }
  return uploaded;
};

// Fire a single test SMS of the current draft to the calling user's own phone.
// Backend (POST /user/portal/test-mass-message) sends to req.user's phone — never trusts a body-supplied number.
export const sendTestMassMessage = createAsyncThunk(
  "test-mass-message",
  async ({ message, attachments }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.post(
        "user/portal/test-mass-message",
        { message, attachments },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Fetch the per-instance daily mass-SMS budget so the UI can warn before a large
// blast (e.g. "240/2500 used today — 47 will be sent"). Best-effort; the BFF's
// counter is per-Cloud-Run-instance and resets when the instance recycles.
export const fetchMassSmsBudget = createAsyncThunk(
  "massMessage/fetchBudget",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        "/comm/portal/get/mass-sms-budget",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Fetch the calling user's mass-message history. BFF scopes by employee record
// id server-side, so the response contains ONLY this user's messages — split
// into { active, inactive } so the UI doesn't need to re-derive the rules.
export const fetchMassMessageHistory = createAsyncThunk(
  "massMessage/fetchHistory",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        "/comm/portal/get/mass-message-history",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data?.data ?? { active: [], inactive: [] };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Fetch the live AI Tone single-select choices from Airtable (proxied by the
// BFF). Used by the AI compose panel so the chip list never goes stale when
// Levi renames / adds / removes a tone in Airtable — no code change required.
// On error we deliberately leave `tones` empty rather than fall back to a
// hardcoded list: a silent default would let the UI drift out of sync with
// Airtable in exactly the failure modes this endpoint was added to prevent.
export const fetchAiTones = createAsyncThunk(
  "massMessage/fetchAiTones",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        "/comm/portal/get/ai-tones",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return Array.isArray(data?.data?.tones) ? data.data.tones : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Fetch the per-fire audit rows for one Group Messages parent (typically a
// recurring record). Each entry mirrors a single sendTimedMassMessages fire:
// sentAt, recipientCount, plus AI Prompt/Tone/Model/GeneratedMessage snapshots
// so a recurring item's history is reconstructable even if the parent's AI
// fields are later edited. BFF scopes by ownership; arg is always the parent
// record ID we just got from fetchMassMessageHistory.
export const fetchMassMessageSends = createAsyncThunk(
  "massMessage/fetchSends",
  async ({ groupMessageId }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        `/comm/portal/get/mass-message-sends?groupMessageId=${encodeURIComponent(groupMessageId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return {
        groupMessageId,
        sends: Array.isArray(data?.data?.sends) ? data.data.sends : [],
      };
    } catch (error) {
      return rejectWithValue({
        groupMessageId,
        message: error.response?.data?.message ?? error.message,
      });
    }
  }
);

// Generate a draft mass-message via Gemini. Returns the suggested text — the
// UI shows it as a preview the user can edit, regenerate, or use as-is. Never
// sends. Backend enforces auth + length caps + system prompt; we only pass the
// user's natural-language ask plus optional tone / audience / current draft.
export const composeMassMessage = createAsyncThunk(
  "massMessage/compose",
  async ({ prompt, tone, audienceHint, currentDraft, personalize, previewRecipientId, previewRecipientNameAndId }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.post(
        "/comm/portal/compose-mass-message",
        { prompt, tone, audienceHint, currentDraft, personalize, previewRecipientId, previewRecipientNameAndId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return data?.data ?? { text: "", finishReason: "STOP", truncated: false, personalized: false, previewMeta: null };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Admin variant of fetchMassMessageHistory. Returns ALL active + recently-
// inactive messages across every sender. BFF gates on the TBAC
// `Mass Message — Admin` row; non-admin callers get a 403 we surface in
// `error` so the admin page can show a clean refusal rather than a blank
// loading state.
export const fetchAdminMassMessageHistory = createAsyncThunk(
  "massMessage/fetchAdminHistory",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        "/comm/portal/admin/mass-message-history",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data?.data ?? { active: [], inactive: [] };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Resolve the live cohort an active mass message will fire to (positions +
// locations + team + manual recipients applied). Owner-or-admin gated server
// side. We key the slice bucket by the parent record id so multiple cards
// can be expanded independently without races.
export const fetchAdminMassMessageRecipients = createAsyncThunk(
  "massMessage/fetchAdminRecipients",
  async ({ recordId }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        `/comm/portal/admin/mass-message-recipients?id=${encodeURIComponent(recordId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return {
        recordId,
        recipients: Array.isArray(data?.data?.recipients) ? data.data.recipients : [],
        count:      Number.isFinite(data?.data?.count) ? data.data.count : 0,
      };
    } catch (error) {
      return rejectWithValue({
        recordId,
        message: error.response?.data?.message ?? error.message,
      });
    }
  }
);

// Edit a scheduled / recurring message. Backend enforces owner-or-admin AND
// the field whitelist (see updateMassMessage in communicationController) so
// the UI only has to surface the editable fields. Caller passes `fields` as
// a partial — anything omitted stays unchanged. Cancellation goes through
// cancelMassMessage, NOT here; updateMassMessage refuses to null out
// Next Scheduled Time to keep cancel a single intent.
export const updateMassMessage = createAsyncThunk(
  "massMessage/update",
  async ({ recordId, fields }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.post(
        "/comm/portal/update-mass-message",
        { recordId, fields },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return { recordId, fields, data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Cancel a single active scheduled / recurring message owned by the caller.
// Server enforces ownership and refuses cancellation of historical sends —
// "history can't be deleted or edited" per spec.
export const cancelMassMessage = createAsyncThunk(
  "massMessage/cancel",
  async ({ recordId }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.post(
        "/comm/portal/cancel-mass-message",
        { recordId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return { recordId, data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message ?? error.message);
    }
  }
);

// Phase 5: fetch snapshotted recipient list for a past (inactive) mass message.
// Returns { sendCount, sends: [{ sendRecId, sentAt, recipients: [{name,phone,status}] }] }
// keyed by group message id. Owner-or-admin gated server-side.
export const fetchPastMassMessageRecipients = createAsyncThunk(
  "massMessage/fetchPastRecipients",
  async ({ recordId }, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const { data } = await instance.get(
        `/comm/portal/get/mass-message-past-recipients?id=${encodeURIComponent(recordId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return {
        recordId,
        sendCount: data?.data?.sendCount ?? 0,
        sends:     Array.isArray(data?.data?.sends) ? data.data.sends : [],
      };
    } catch (error) {
      return rejectWithValue({
        recordId,
        message: error.response?.data?.message ?? error.message,
      });
    }
  }
);

export const scheduleMassMessage = createAsyncThunk(
  "create-mass-message",
  async (
    {
      message,
      locations,
      positions,
      categories,
      recipients,
      scheduledTime,
      recurring,
      recurringEndDate,
      daysOfWeek,
      sendTime,
      attachments,
      aiPrompt,    // optional — only sent when the user opted into "save prompt for recurring"
      aiTone,      // optional — paired with aiPrompt; recurring cron uses both at fire time
      personalizePerRecipient, // optional boolean — paired with aiPrompt; cron generates a unique body per recipient at fire time
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const { token } = getState().auth;
      const config = {
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      };
      const { data } = await instance.post(
        "user/portal/create-mass-message",
        {
          message,
          locations,
          positions,
          categories,
          recipients,
          scheduledTime,
          recurring,
          recurringEndDate,
          daysOfWeek,
          sendTime,
          attachments,
          aiPrompt,
          aiTone,
          personalizePerRecipient,
        },
        config
      );

      return data;
    } catch (error) {
      if (error.response && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);